import { AnyAction, PayloadAction } from '@reduxjs/toolkit';
import { call, put, takeEvery, select } from 'redux-saga/effects';
import { ApiError, ApiResponse, IError } from '../../../../../base/api';
import { Loading } from '../../../../../base/enums';
import { baseErrorHandler } from '../../../../../base/redux/sagas/appSaga';
import { appActions } from '../../../../../base/redux/slices/appSlice';
import { RootState } from '../../../../../base/redux/store';
import { isEmptyObject } from '../../../../../base/utils';
import { employerAppActions } from '../../../../redux/slices/employerAppSlice';
import { vacancyApi } from '../../api';
import { PublishVacancyResponse, VacancyResponseDTO, ValidateVacancyRequestParams } from '../../api/interfaces';
import {
    isTagChecked,
    mapTagIdToCheckbox,
    prepareDataByStep,
    prepareDataToUpload,
    validateOnClient,
} from '../../utils';
import { Fields, InitialVacancyDataParams, vacancyFormActions, VacancyFormState } from '../slices/vacancyFormSlice';

function* init({ payload: vacancyId }: AnyAction) {
    try {
        yield put(appActions.setLoading(Loading.App));

        if (vacancyId) {
            yield put(vacancyFormActions.setVacancyId(vacancyId));
            const { data }: ApiResponse<VacancyResponseDTO> = yield call(vacancyApi.getVacancyDataById, vacancyId);
            const convertedTags = mapTagIdToCheckbox(data.tagIds);
            const convertedData: InitialVacancyDataParams = {
                name: data.name,
                forRecruiters: data.forRecruiters,
                forWorkers: data.forWorkers,
                suitableForMen: isTagChecked(convertedTags, 'suitableForMen'),
                suitableForWomen: isTagChecked(convertedTags, 'suitableForWomen'),
                suitableForCouple: isTagChecked(convertedTags, 'suitableForCouple'),
                suitableForStudents: isTagChecked(convertedTags, 'suitableForStudents'),
                rateType: data.rateType,
                rateFrom: data.rateFrom ? data.rateFrom.toString() : '',
                rateTo: data.rateTo ? data.rateTo.toString() : '',
                hoursPerMonthFrom: data.hoursPerMonthFrom ? data.hoursPerMonthFrom.toString() : '',
                hoursPerMonthTo: data.hoursPerMonthTo ? data.hoursPerMonthTo.toString() : '',
                currencyId: data.currencyId,
                isHotVacancy: isTagChecked(convertedTags, 'isHotVacancy'),
                isExperienceNeeded: isTagChecked(convertedTags, 'isExperienceNeeded'),
                isPassportOnly: isTagChecked(convertedTags, 'isPassportOnly'),
                isAccomodationProvided: isTagChecked(convertedTags, 'isAccomodationProvided'),
                isFoodProvided: isTagChecked(convertedTags, 'isFoodProvided'),
                isTransferProvided: isTagChecked(convertedTags, 'isTransferProvided'),
                isClothesProvided: isTagChecked(convertedTags, 'isClothesProvided'),
                isSeasonalWork: isTagChecked(convertedTags, 'isSeasonalWork'),
                isRemoteWork: isTagChecked(convertedTags, 'isRemoteWork'),
                isQuickApplyAvailable: isTagChecked(convertedTags, 'isQuickApplyAvailable'),
                city: data.city,
                description: data.description,
                images: data.images,
                requiredLanguageId: data.requiredLanguageId,
                specialities: data.specialities,
                areas: [data.area],
                workersCount: data.workersCount ? data.workersCount.toString() : '1',
                views: data.views || null,
                updatedAt: data.updatedAt || null,
            };

            yield put(employerAppActions.citySearchSuccess([data.city]));
            yield put(vacancyFormActions.setInitialVacancyData(convertedData));
        }
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* publishVacancy() {
    try {
        yield put(appActions.setLoading(Loading.App));
        const { fields, vacancyId, duplicate }: VacancyFormState = yield select(
            (state: RootState) => state.vacancyForm,
        );
        const uploadData = prepareDataToUpload(fields);
        if (!vacancyId || (vacancyId && duplicate)) {
            const {
                data: { id },
            }: ApiResponse<PublishVacancyResponse> = yield call(vacancyApi.publishVacancy, uploadData);
            if (fields.imagesToUpload.length) {
                yield call(vacancyApi.saveImages, id, fields.imagesToUpload);
            }
            window.location.href = `/vacancy/confirmation/${id}`;
        } else {
            yield call(vacancyApi.updateVacancy, { id: vacancyId, data: uploadData });
            if (fields.imagesToUpload.length) {
                yield call(vacancyApi.saveImages, vacancyId, fields.imagesToUpload);
            }
            window.location.href = `/vacancy/view/${vacancyId}`;
        }
    } catch (e) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            yield put(vacancyFormActions.setErrors(e.errors));
            return;
        }
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* nextStep() {
    let errors: IError | null = null;
    try {
        yield put(appActions.setLoading(Loading.App));
        const { step, fields } = yield select((state: RootState) => state.vacancyForm);

        errors = yield validateOnClient(fields, step);
        const dataForBackendValidation: ValidateVacancyRequestParams = yield prepareDataByStep(fields, step);
        yield call(vacancyApi.validateStep, dataForBackendValidation);

        if (errors) {
            yield put(vacancyFormActions.setErrors(errors));
            yield put(vacancyFormActions.setShouldScrollToError(true));
        } else {
            if (step < 4) {
                yield put(vacancyFormActions.setStep(step + 1));
            } else {
                yield call(publishVacancy);
            }
        }
    } catch (e) {
        if (e instanceof ApiError && e.errors && !isEmptyObject(e.errors)) {
            let mergedErrors = e.errors;
            if (errors) {
                mergedErrors = {
                    ...mergedErrors,
                    ...errors,
                };
            }
            yield put(vacancyFormActions.setErrors(mergedErrors));
            yield put(vacancyFormActions.setShouldScrollToError(true));
            return;
        }
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

function* deleteImageById({ payload: { vacancyId, imageId } }: PayloadAction<{ vacancyId: string; imageId: number }>) {
    try {
        yield put(appActions.setLoading(Loading.App));
        yield call(vacancyApi.deleteImageById, vacancyId, imageId);
        const { images }: Fields = yield select((state: RootState) => state.vacancyForm.fields);
        const filteredImages = images.filter((image) => image.id !== imageId);
        yield put(vacancyFormActions.setField({ id: 'images', value: filteredImages }));
    } catch (e) {
        yield call(baseErrorHandler, e);
    } finally {
        yield put(appActions.setLoading(null));
    }
}

export function* vacancyFormSaga() {
    yield takeEvery(vacancyFormActions.init, init);
    yield takeEvery(vacancyFormActions.nextStep, nextStep);
    yield takeEvery(vacancyFormActions.deleteImageById, deleteImageById);
}
